import { PlusCircleFilled } from "@ant-design/icons";
import { Alert, Form, Select } from "antd";
import { t } from "i18next";
import { observer } from "mobx-react-lite";
import React, { useEffect, useMemo } from "react";
import { ZObjectRefSelector } from "src/common/attrEdit/components";
import { onError } from "src/common/onError";
import { loadObjectStatesInsideRefLikeAtts } from "src/pages/ManagementPage/Obj2Tab/roles/rolesApi";
import { ZUid } from "src/types/zUid";
import { ifDef } from "src/common/ifDef";
import { EntitySelectTable } from "../EntitySelectTable";
import styles from "./ObjectRefSelector.module.less";
import { ObjectRefSelectorLabel } from "./ObjectRefSelectorLabel";
import { ObjectRefSelectorStore } from "./ObjectRefSelectorStore";

const getUniqToSelect = (value: string[], store: ObjectRefSelectorStore) =>
  Array.from(
    new Map(
      [
        ...(value || []),
        ...(
          store.entitySelectStore?.filtersPageStore?.tableStore?.selected || []
        ).map((e) => e.id),
      ].map((e) => [e, e]),
    ).values(),
  );

type PropsObjectRefSelector = {
  store: ObjectRefSelectorStore;
  component: ZObjectRefSelector;
  value?: string[];
  disabled?: boolean;
  onChange?: (value?: string[]) => void;
  label: string;
  objectName?: string;
  entityId?: ZUid;
};

export const ObjectRefSelector: React.FC<PropsObjectRefSelector> = observer(
  ({
    component,
    value,
    disabled,
    onChange,
    label,
    objectName,
    entityId,
    store,
  }) => {
    const { entitySelectStore, attribute } = store;
    const { referenceId } = attribute;
    const statesLoader = useMemo(
      () =>
        ifDef(referenceId, (id) => () => loadObjectStatesInsideRefLikeAtts(id)),
      [referenceId],
    );
    const form = Form.useFormInstance();
    const filterField: string[] | null | undefined = Form.useWatch(
      String(attribute.filter),
      form,
    );
    // Итератор используется только для создания новых экземпляров. LPLM-1282
    const isIterator = !!attribute.iterator && !entityId;

    // Если компонент в принципе может использоваться для множественного создания, то параметр из описания компонента игнорируется
    const multiple = attribute.iterator ? isIterator : !!component.multiple;

    useEffect(() => {
      store
        .init(attribute, component, multiple, form, value, onChange, entityId)
        .catch(onError);
    }, [attribute.referenceId, attribute.backlinkAttributeId, entityId]);

    const selectDisabled =
      (attribute.filter && !filterField?.length) ||
      (!!attribute.backlinkAttributeId && !entityId) ||
      disabled;

    const toSelect = getUniqToSelect(value || [], store);
    const labelsMap = store.calculatedLabelsMap;
    const options = toSelect.map((eId) => ({
      label: (
        <ObjectRefSelectorLabel
          component={store.component}
          entityId={eId}
          entityLabel={labelsMap[eId]}
        />
      ),
      value: String(eId),
    }));

    return (
      <>
        <Select
          key={`$refSelect-${value?.join("-")}`}
          disabled={selectDisabled}
          className={styles.select}
          open={false}
          value={value}
          loading={store.loading}
          mode={multiple ? "multiple" : undefined}
          onClick={() => {
            if (!selectDisabled) entitySelectStore?.setOpen(true);
          }}
          showSearch={false}
          options={options}
          suffixIcon={!selectDisabled && <PlusCircleFilled />}
          onChange={(list) => {
            onChange?.(list);
          }}
        />
        {isIterator && (
          <Alert
            type="warning"
            showIcon
            message={t(
              "When saving, multiple instances of the object will be created",
              { attr: label, obj: objectName ?? "?" },
            )}
          />
        )}
        {entitySelectStore && (
          <EntitySelectTable
            store={entitySelectStore}
            label={label}
            form={form}
            disabled={selectDisabled}
            value={value}
            statesLoader={statesLoader}
            makeTag={(id) => (
              <ObjectRefSelectorLabel
                component={store.component}
                entityId={id}
                entityLabel={labelsMap[id]}
              />
            )}
          />
        )}
      </>
    );
  },
);
